import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "./ui/select";
import { FileText, ImageSquare, Trash, UploadSimple, MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
    parcelle: { label: "Photo parcelle", icon: MapTrifold, color: "bg-terracotta text-white" },
    contrat: { label: "Contrat", icon: FileText, color: "bg-sage text-white" },
    autre: { label: "Autre", icon: FileText, color: "bg-sand text-ink" },
};

export default function ClientFiles({ clientId }) {
    const [files, setFiles] = useState([]);
    const [kind, setKind] = useState("parcelle");
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    async function load() {
        const { data } = await api.get(`/clients/${clientId}/files`);
        setFiles(data);
    }
    useEffect(() => {
        load();
    }, [clientId]);

    async function onFileSelected(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("Fichier trop volumineux (max 10 Mo)");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            await api.post(`/clients/${clientId}/files?kind=${kind}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Fichier ajouté");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
        }
    }

    async function removeFile(id) {
        try {
            await api.delete(`/files/${id}`);
            toast.success("Fichier supprimé");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }

    return (
        <div className="bento-card fade-up">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="font-display text-xl font-bold text-ink">Documents</h3>
                <div className="flex items-center gap-2">
                    <Select value={kind} onValueChange={setKind}>
                        <SelectTrigger className="w-44" data-testid="file-kind-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="parcelle">Photo parcelle</SelectItem>
                            <SelectItem value="contrat">Contrat</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                    </Select>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={onFileSelected}
                        className="hidden"
                        data-testid="file-input"
                    />
                    <Button
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="bg-terracotta hover:bg-terracotta-dark text-white"
                        data-testid="upload-file-button"
                    >
                        <UploadSimple size={16} className="mr-1.5" />
                        {uploading ? "Envoi…" : "Téléverser"}
                    </Button>
                </div>
            </div>

            {files.length === 0 ? (
                <p className="text-sm text-ink-soft py-6 text-center">
                    Aucun document. Téléversez photo de parcelle, contrat signé, etc.
                </p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {files.map((f) => {
                        const meta = KIND_META[f.kind] || KIND_META.autre;
                        const Icon = meta.icon;
                        const isImage = (f.content_type || "").startsWith("image/");
                        const url = `${BACKEND_URL}/api/files/${f.id}/download`;
                        return (
                            <div
                                key={f.id}
                                className="group rounded-md border border-sand bg-cream overflow-hidden"
                                data-testid={`file-card-${f.id}`}
                            >
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block aspect-square bg-white grid place-items-center relative overflow-hidden"
                                >
                                    {isImage ? (
                                        <img
                                            src={url}
                                            alt={f.original_filename}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <Icon size={42} className="text-terracotta" weight="duotone" />
                                    )}
                                </a>
                                <div className="p-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                                        <button
                                            onClick={() => removeFile(f.id)}
                                            className="text-ink-soft hover:text-destructive"
                                            data-testid={`delete-file-${f.id}`}
                                        >
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                    <p className="text-xs text-ink mt-1.5 truncate" title={f.original_filename}>
                                        {f.original_filename}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
